// The one footer every paged register shares: the pager on the left, the rows
// per page and "Rândurile 1–25 din 1 206" on the right — the wireframe's
// `.tbl-foot` (mud.css:2806, pagination left and count right by the owner's
// 2026-08-24 ruling at :2818). It sticks to the bottom of the viewport, as the
// wireframe's does, so the pager is on screen whatever the page size; at 50
// rows of 48px it used to sit 2 400px below the fold (owner, 2026-09-15).
//
// The rows-per-page choice is MUD's: pagination is documented with a
// contextual menu of numbers attached (mud/components/pagination.md
// §Page-size selector). One default and one set of choices for every register,
// so no table invents its own count again (they were 6, 10, 25 and 50).
import { useCallback, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { MudPagination, MudSelect } from '@egov-moldova/mud-react';
import { formatNumber } from '@simsm/ui-shared';

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 25;

const STORAGE_PREFIX = 'simsm.page-size.';

function readPageSize(key: string): number {
  try {
    const stored = Number(window.localStorage.getItem(STORAGE_PREFIX + key));
    return (PAGE_SIZE_OPTIONS as readonly number[]).includes(stored) ? stored : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
}

/** The rows per page of one register, remembered in this browser under `key`. */
export function usePageSize(key: string): [number, (size: number) => void] {
  const [size, setSize] = useState(() => readPageSize(key));
  const update = useCallback(
    (next: number) => {
      setSize(next);
      try {
        window.localStorage.setItem(STORAGE_PREFIX + key, String(next));
      } catch {
        /* a private-mode browser still pages, it just forgets the choice */
      }
    },
    [key],
  );
  return [size, update];
}

export interface TableFooterProps {
  total: number;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}

export function TableFooter({
  total,
  page,
  pageSize,
  onPage,
  onPageSize,
}: TableFooterProps): JSX.Element {
  const { t } = useTranslate();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), pages);
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(current * pageSize, total);

  function changePageSize(next: number): void {
    onPageSize(next);
    // The page that still holds the first row the reader was looking at, so a
    // bigger or smaller page does not throw them back to the start.
    onPage(from > 0 ? Math.floor((from - 1) / next) + 1 : 1);
  }

  return (
    <div className="simsm-table-foot">
      {/* AGE draws nothing at `totalPages <= 1`, so a one-page register shows
          the count alone. */}
      <div className="simsm-table-foot__pager">
        <MudPagination
          size="md"
          currentPage={current}
          totalPages={pages}
          onMudChange={(e: CustomEvent<{ page: number }>) => onPage(e.detail.page)}
        />
      </div>
      <div className="simsm-table-foot__end">
        {/* At ten rows or fewer every choice shows them all — nothing to choose. */}
        {total > PAGE_SIZE_OPTIONS[0] ? (
          <div className="simsm-table-foot__size">
            <span aria-hidden="true">{t('common.table.page_size')}</span>
            <MudSelect
              size="medium"
              aria-label={t('common.table.page_size')}
              value={String(pageSize)}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                changePageSize(Number(e.detail.value))
              }
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </MudSelect>
          </div>
        ) : null}
        <p className="simsm-table-foot__count">
          {total === 0
            ? t('common.table.none')
            : t('common.table.range', {
                from: formatNumber(from),
                to: formatNumber(to),
                total: formatNumber(total),
              })}
        </p>
      </div>
    </div>
  );
}
